/* eslint-disable jsx-a11y/no-static-element-interactions */
import Slider from '@/components/Slider';
import useIsMobile from '@/hooks/useIsMobile';
import './style.css';
import { useQuery } from '@apollo/client';
import Button from '@/components/Button';
import dayjs from 'dayjs';
import {
  Enum_Favorite_Type,
  FetchWildcardsQuery,
  FetchWildcardsQueryVariables,
  WildcardEntity,
} from '@/graphql/generated/graphql';
import LikeButton from '@/components/LikeButton';
import { buildRouteUrl, getFirstValidRoute } from '@/utils/navigation';
import SubContainer from '@/components/Container/SubContainer';
import { QUERY_FETCH_WILDCARDS } from '@/graphql/queries/wildcard/wildcard';
import classNames from 'classnames';

export const gradients = {
  insight: 'insight-class',
  mindful: 'mindful-class',
  emotion: 'emotion-class',
  quote: 'quote-class',
  wildcard: 'wildcard-class',
};
interface WildcardsProps {
  renderTitle: () => JSX.Element;
}

export default function Wildcards({ renderTitle }: WildcardsProps) {
  const { isMobile } = useIsMobile();

  const { data: wildcardsData, loading } = useQuery<FetchWildcardsQuery, FetchWildcardsQueryVariables>(
    QUERY_FETCH_WILDCARDS,
    {
      variables: { today: dayjs().format('YYYY-MM-DD') },
    },
  );

  const hasContent = wildcardsData?.wildcards?.data && wildcardsData?.wildcards?.data.length > 0;

  if (!loading && !hasContent) {
    return <></>;
  }

  const renderCardMobile = (wildCard: WildcardEntity) => {
    const wildcardAttributes = wildCard?.attributes;

    let wildcardHref = '#';
    const route = getFirstValidRoute(wildCard?.attributes?.cta);
    if (route) {
      const href = buildRouteUrl(route);
      wildcardHref = href;
    }

    return (
      <div className="relative h-[428px] w-[335px] overflow-hidden rounded-2xl border-[5px] border-white bg-[#D3E4F5]">
        <div className="absolute left-1/2 top-[25px] z-10 flex size-32 -translate-x-1/2 items-center justify-center overflow-hidden rounded-full">
          <img
            src={wildcardAttributes?.image?.data?.attributes?.url}
            alt="logo"
            className={`h-[125px] w-[125px] rounded-full object-cover object-center`}
          />
        </div>
        <div
          className=" absolute -bottom-[100px] left-1/2 size-[440px] -translate-x-1/2 rounded-t-full bg-white"
          style={{
            background: 'radial-gradient(50% 50% at 50% 50%, #D0E2F5 0%, #FFF 100%)',
          }}>
          <div className=" px-[80px] pt-[100px] text-center text-sh-xxs">
            <h3 className="!m-0 text-center font-larken text-sh-18-28 md:text-start">{wildcardAttributes?.title}</h3>
            <p className="mt-2 text-center text-sh-12-18 md:text-start">{wildcardAttributes?.description}</p>
            {wildCard?.attributes?.cta && (
              <Button as="a" className="mt-4 text-center" href={wildcardHref}>
                {wildcardAttributes?.ctaText}
              </Button>
            )}
            {!wildcardAttributes?.cta && (
              <LikeButton
                isButtonLayout
                label={wildcardAttributes?.ctaText}
                entityId={wildCard!.id || ''}
                type={Enum_Favorite_Type.Wildcard}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCardDesktop = (wildCard: WildcardEntity) => {
    const wildcardAttributes = wildCard?.attributes;

    let wildcardHref = '#';
    const route = getFirstValidRoute(wildCard?.attributes?.cta);
    if (route) {
      const href = buildRouteUrl(route);
      wildcardHref = href;
    }

    return (
      <div
        className={classNames(
          'group h-full w-[300px] overflow-hidden rounded-2xl border-2 border-transparent md:w-full',
        )}>
        <div
          className={classNames(
            'item flex h-full flex-col justify-center px-sh-30 py-sh-15 ' +
              `daily-bubble-radial ${gradients['wildcard']}`,
          )}>
          <div className="flex  flex-row items-center rounded-md">
            <div className="mr-[30px] max-h-[125px] min-h-[125px] min-w-[125px] max-w-[125px] overflow-hidden rounded-full">
              {wildCard.attributes?.internal && (
                <div
                  className={`h-[125px] w-[125px] bg-cover bg-center`}
                  style={{
                    backgroundImage: `url('${wildcardAttributes?.image?.data?.attributes?.url}')`,
                    transformOrigin: 'center',
                  }}
                />
              )}
              {!wildCard.attributes?.internal && (
                <div className="flex h-[125px] w-[125px] items-center justify-center rounded-full bg-white">
                  <img
                    src={wildcardAttributes?.image?.data?.attributes?.url}
                    alt="logo"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col items-center md:items-start">
              <h3 className="!m-0 text-center font-larken text-sh-18-28 md:text-start">{wildcardAttributes?.title}</h3>
              <p className="mt-2 text-center text-sh-12-18 md:text-start">{wildcardAttributes?.description}</p>
              {wildCard?.attributes?.cta && (
                <Button as="a" className="mt-2 text-center" href={wildcardHref}>
                  {wildcardAttributes?.ctaText}
                </Button>
              )}
              {!wildcardAttributes?.cta && (
                <LikeButton
                  isButtonLayout
                  label={wildcardAttributes?.ctaText}
                  entityId={wildCard!.id || ''}
                  type={Enum_Favorite_Type.Wildcard}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return [<div key="loading" className="h-[168px] w-[610px] animate-pulse rounded-md bg-gray-300"></div>];
    }

    if (!wildcardsData?.wildcards?.data?.length) {
      return [];
    }

    return wildcardsData.wildcards.data.map((wildcard) => renderCardDesktop(wildcard as WildcardEntity));
  };

  const renderMobileContent = () => {
    if (loading) {
      return [<div key="loading" className="h-[168px] w-[610px] animate-pulse rounded-md bg-gray-300"></div>];
    }

    if (!wildcardsData?.wildcards?.data?.length) {
      return [];
    }

    return wildcardsData.wildcards.data.map((wildcard) => renderCardMobile(wildcard as WildcardEntity));
  };

  if (!isMobile) {
    return (
      <>
        <div className="mt-[30px] w-full">{renderTitle()}</div>
        <SubContainer className="md:flex md:gap-4">
          <div className="grid grid-cols-1 gap-[16px] sm:grid-cols-2">{renderContent()}</div>
        </SubContainer>
      </>
    );
  }

  return (
    <>
      <div className="mt-[30px] w-full">{renderTitle()}</div>
      <SubContainer className="md:flex md:gap-4">
        {hasContent && (
          <Slider
            disableResponsiveDots={true}
            slideClassName="!max-h-none"
            sliders={renderMobileContent()}
            options={{
              disabled: true,
              breakpoints: {
                '(max-width: 768px)': {
                  disabled: false,
                  loop: true,
                  renderMode: 'performance',
                  slides: { perView: 1, spacing: 16 },
                },
              },
            }}
          />
        )}
        {!hasContent && renderMobileContent()}
      </SubContainer>
    </>
  );
}
